import { useEffect, useRef, useState } from 'preact/hooks';
import { UserProfileIcon } from '@components/avatars/user-profile-icon';
import { timeago } from 'shared/dateutil';
import { MIRASEE_STAFF_TITLES } from 'shared/mirasee-consts';
import { MiraseeBadge } from '@components/mirasee-badge';
import { PostCard } from './post-card';
import type { CommunityDiscussion } from './types';

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" style="width: 14px; height: 14px; flex-shrink: 0">
      <path d="M16 4h-2V2h-4v2H8c-.55 0-1 .45-1 1v4l-2 3v2h5.97v7l1 1 1-1v-7H19v-2l-2-3V5c0-.55-.45-1-1-1z" />
    </svg>
  );
}

function ChevronIcon({ up }: { up: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      style={`width: 14px; height: 14px; transition: transform 0.2s ease; ${up ? 'transform: rotate(180deg)' : ''}`}
    >
      <path
        fill-rule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
        clip-rule="evenodd"
      />
    </svg>
  );
}

function stripHtml(html: string) {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

function PinnedCard({
  discussion,
  isExpanded,
  onClick,
}: {
  discussion: CommunityDiscussion;
  isExpanded: boolean;
  onClick: () => void;
}) {
  const { author } = discussion;
  const isMirasee = discussion.authorLevel === 'guide' || discussion.authorLevel === 'facilitator';
  const staffTitle = MIRASEE_STAFF_TITLES[author.id];
  const bodyText = discussion.description ? stripHtml(discussion.description) : '';

  return (
    <button
      type="button"
      class={`text-left rounded-2xl p-4 border transition cursor-pointer ${
        isExpanded
          ? 'border-indigo-300 dark:border-indigo-600 bg-indigo-50/80 dark:bg-indigo-950/40 ring-1 ring-indigo-200/60 dark:ring-indigo-700/40'
          : 'border-indigo-200/60 dark:border-indigo-800/40 bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-indigo-950/40 dark:via-gray-800 dark:to-violet-950/30 hover:border-indigo-300 dark:hover:border-indigo-700'
      }`}
      onClick={onClick}
    >
      <div class="flex items-center gap-1.5 mb-2 text-indigo-500 dark:text-indigo-400">
        <PinIcon />
        <span class="text-xs font-semibold uppercase tracking-wider">Pinned</span>
      </div>
      <div class="flex items-center gap-2 mb-2">
        <UserProfileIcon user={author} size="w-7 h-7 text-xs" maxWidth={56} />
        <div class="flex items-center gap-1.5 min-w-0">
          <span class="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {author.name}
          </span>
          {isMirasee && <MiraseeBadge />}
        </div>
        <span class="text-xs text-gray-400 dark:text-gray-500 shrink-0 ml-auto">
          {timeago(discussion.createdAt, ' ago')}
        </span>
      </div>
      {discussion.prompt && (
        <p class="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 mb-1">
          {discussion.prompt}
        </p>
      )}
      {bodyText && <p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-4">{bodyText}</p>}
      {staffTitle && <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">{staffTitle}</p>}
      <div class="flex justify-end mt-2 text-indigo-400 dark:text-indigo-500">
        <ChevronIcon up={isExpanded} />
      </div>
    </button>
  );
}

type Props = {
  pinned: CommunityDiscussion[];
  canPin: boolean;
  canHide: boolean;
  onPinToggled: () => void;
  onHideToggled: () => void;
  onCommentAdded: () => void;
  onPostEdited: () => void;
};

export function PinnedPostsGrid({
  pinned,
  canPin,
  canHide,
  onPinToggled,
  onHideToggled,
  onCommentAdded,
  onPostEdited,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | undefined>();
  const expandedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expandedId && expandedRef.current) {
      expandedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [expandedId]);

  if (pinned.length === 0) {
    return undefined;
  }

  const expandedDiscussion = expandedId ? pinned.find((d) => d.id === expandedId) : undefined;

  const gridCols =
    pinned.length === 1
      ? 'grid-cols-1'
      : pinned.length === 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div class="mb-4">
      <div class={`grid ${gridCols} gap-3`}>
        {pinned.map((discussion) => (
          <PinnedCard
            key={discussion.id}
            discussion={discussion}
            isExpanded={expandedId === discussion.id}
            onClick={() => setExpandedId(expandedId === discussion.id ? undefined : discussion.id)}
          />
        ))}
      </div>
      {expandedDiscussion && (
        <div ref={expandedRef} class="mt-3 pinned-expand">
          <button
            type="button"
            class="flex items-center justify-center gap-1.5 w-full py-1.5 mb-2 text-xs font-medium text-indigo-500 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition cursor-pointer"
            onClick={() => setExpandedId(undefined)}
          >
            <ChevronIcon up={true} />
            <span>Collapse</span>
          </button>
          <PostCard
            key={expandedDiscussion.id}
            discussion={expandedDiscussion}
            defaultBodyExpanded
            onCommentAdded={onCommentAdded}
            onPostEdited={onPostEdited}
            canPin={canPin}
            onPinToggled={() => {
              setExpandedId(undefined);
              onPinToggled();
            }}
            canHide={canHide}
            onHideToggled={onHideToggled}
          />
        </div>
      )}
    </div>
  );
}
